import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  TextField, 
  Button, 
  Paper, 
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import { initializeAdminEmails } from '../../scripts/initializeAdminEmails';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Temporary page to initialize admin emails in Firestore
 * This page should only be accessible during initial setup
 */
const AdminInitPage: React.FC = () => {
  const { state } = useAuth();
  const [globalEmails, setGlobalEmails] = useState<string[]>([
    'admin@strategiz.io',
    'test@gmail.com',
    'test1@gmail.com',
    'test2@gmail.com'
  ]);
  const [devEmails, setDevEmails] = useState<string[]>([
    'admin@strategiz.io',
    'test@gmail.com',
    'test1@gmail.com',
    'test2@gmail.com'
  ]);
  const [prodEmails, setProdEmails] = useState<string[]>([
    'admin@strategiz.io',
    'test@gmail.com',
    'test1@gmail.com',
    'test2@gmail.com'
  ]);
  const [activeTab, setActiveTab] = useState<'global' | 'dev' | 'prod'>('global');
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get the active email list based on the current tab
  const getActiveEmailList = () => {
    switch (activeTab) {
      case 'global':
        return globalEmails;
      case 'dev':
        return devEmails;
      case 'prod':
        return prodEmails;
      default:
        return globalEmails;
    }
  };

  // Set the active email list based on the current tab
  const setActiveEmailList = (emails: string[]) => {
    switch (activeTab) {
      case 'global':
        setGlobalEmails(emails);
        break;
      case 'dev':
        setDevEmails(emails);
        break;
      case 'prod':
        setProdEmails(emails);
        break;
    }
  };

  const handleAddEmail = () => {
    if (!newEmail || !newEmail.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    
    const currentEmails = getActiveEmailList();
    
    if (currentEmails.includes(newEmail)) {
      setError('This email is already in the list');
      return;
    }
    
    setActiveEmailList([...currentEmails, newEmail]);
    setNewEmail('');
    setError(null);
  };

  const handleRemoveEmail = (email: string) => {
    const currentEmails = getActiveEmailList();
    setActiveEmailList(currentEmails.filter(e => e !== email));
  };

  const handleInitialize = async () => {
    // Validate all email lists
    if (globalEmails.length === 0) {
      setError('At least one global admin email is required');
      return;
    }
    if (devEmails.length === 0) {
      setError('At least one development admin email is required');
      return;
    }
    if (prodEmails.length === 0) {
      setError('At least one production admin email is required');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      await initializeAdminEmails({
        globalEmails,
        devEmails,
        prodEmails
      });
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to initialize admin emails');
    } finally {
      setLoading(false);
    }
  };

  // Make sure the user is authenticated
  if (!state.user) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Alert severity="error">
          You must be logged in to access this page.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Initialize Admin Access
        </Typography>
        
        <Box sx={{ display: 'flex', mb: 3 }}>
          <Button 
            variant={activeTab === 'global' ? 'contained' : 'outlined'}
            onClick={() => setActiveTab('global')}
            sx={{ mr: 1 }}
          >
            Global Admins
          </Button>
          <Button 
            variant={activeTab === 'dev' ? 'contained' : 'outlined'}
            onClick={() => setActiveTab('dev')}
            sx={{ mr: 1 }}
          >
            Dev Admins
          </Button>
          <Button 
            variant={activeTab === 'prod' ? 'contained' : 'outlined'}
            onClick={() => setActiveTab('prod')}
          >
            Production Admins
          </Button>
        </Box>
        
        <Typography variant="body1" paragraph>
          This page allows you to set up the initial list of admin emails in Firestore.
          Once initialized, you can manage admin access from the Settings page.
        </Typography>
        
        <Alert severity="warning" sx={{ mb: 3 }}>
          This action will overwrite any existing admin email configuration.
          Make sure to include your own email in the list!
        </Alert>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Admin emails initialized successfully! You can now access admin features.
          </Alert>
        )}
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            {activeTab === 'global' ? 'Global Admin Emails:' : 
             activeTab === 'dev' ? 'Development Admin Emails:' : 
             'Production Admin Emails:'}
          </Typography>
          
          <List>
            {getActiveEmailList().map((email) => (
              <ListItem key={email} divider>
                <ListItemText primary={email} />
                <Button 
                  color="error" 
                  onClick={() => handleRemoveEmail(email)}
                  disabled={loading}
                >
                  Remove
                </Button>
              </ListItem>
            ))}
          </List>
        </Box>
        
        <Box sx={{ display: 'flex', mb: 3 }}>
          <TextField
            label="Add Email"
            variant="outlined"
            fullWidth
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            disabled={loading}
          />
          <Button
            variant="contained"
            onClick={handleAddEmail}
            disabled={loading || !newEmail}
            sx={{ ml: 1 }}
          >
            Add
          </Button>
        </Box>
        
        <Divider sx={{ mb: 3 }} />
        
        <Button
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          onClick={handleInitialize}
          disabled={loading || globalEmails.length === 0 || devEmails.length === 0 || prodEmails.length === 0}
        >
          {loading ? 'Initializing...' : 'Initialize Admin Emails'}
        </Button>
      </Paper>
    </Container>
  );
};

export default AdminInitPage;
